import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.echomemoir.ai/api';

const api = axios.create({
  baseURL: API_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  updateMe: (data) => api.put('/auth/me', data),
  changePassword: (data) => api.post('/auth/change-password', data),
  // Password reset
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (token, data) => api.post(`/auth/reset-password/${token}`, data),
  verifyResetToken: (token) => api.get(`/auth/reset-password/${token}`),
  // Email confirmation
  confirmEmail: (token) => api.post(`/auth/confirm-email/${token}`),
  resendConfirmation: () => api.post('/auth/resend-confirmation'),
  // Account deletion
  deleteAccount: (data) => api.delete('/auth/account', { data }),
  // Profile photo
  uploadProfilePhoto: (formData) => api.post('/auth/me/photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getProfilePhoto: () => api.get('/auth/me/photo'),
  deleteProfilePhoto: () => api.delete('/auth/me/photo'),
};

// Family Circles
export const familyAPI = {
  getAll: () => api.get('/family'),
  getOne: (id) => api.get(`/family/${id}`),
  create: (data) => api.post('/family', data),
  update: (id, data) => api.put(`/family/${id}`, data),
  uploadAvatar: (id, formData) =>
    api.post(`/family/${id}/avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  getAvatar: (id) => api.get(`/family/${id}/avatar`),
  createInvite: (id, data) => api.post(`/family/${id}/invite`, data),
  getInviteDetails: (token) => api.get(`/family/invite/${token}`),
  joinWithInvite: (token, data) => api.post(`/family/join/${token}`, data),
  getMembers: (id) => api.get(`/family/${id}/members`),
  changeMemberRole: (familyId, userId, role) => 
    api.put(`/family/${familyId}/members/${userId}/role`, { role }),
  removeMember: (familyId, userId) => 
    api.delete(`/family/${familyId}/members/${userId}`),
  // Family tree
  getTree: (id) => api.get(`/family/${id}/tree`),
  updateTreeMember: (familyId, userId, data) => 
    api.put(`/family/${familyId}/tree/${userId}`, data),
  // Tree-only members (non-users)
  addTreeMember: (familyId, data) =>
    api.post(`/family/${familyId}/tree/add`, data),
  updateTreeOnlyMember: (familyId, memberId, data) =>
    api.put(`/family/${familyId}/tree/only/${memberId}`, data),
  deleteTreeOnlyMember: (familyId, memberId) =>
    api.delete(`/family/${familyId}/tree/only/${memberId}`),
};

// Stories
export const storiesAPI = {
  getByFamily: (familyCircleId, params) => 
    api.get(`/stories/family/${familyCircleId}`, { params }),
  getOne: (id) => api.get(`/stories/${id}`),
  getStatus: (id) => api.get(`/stories/${id}/status`),
  getAudioUrl: (id) => api.get(`/stories/${id}/audio-url`),
  create: (data) => api.post('/stories', data),
  uploadAudio: (id, formData) => 
    api.post(`/stories/${id}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  quickUpload: (formData) => 
    api.post('/stories/quick', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  quickVideoUpload: (formData) =>
    api.post('/stories/quick-video', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  getVideoUrl: (id) => api.get(`/stories/${id}/video-url`),
  transcribe: (formData) =>
    api.post('/stories/transcribe', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  enhance: (id) => api.post(`/stories/${id}/enhance`),
  updateTitle: (id, title) => api.put(`/stories/${id}/title`, { title }),
  delete: (id) => api.delete(`/stories/${id}`),
  uploadPhotos: (id, formData) =>
    api.post(`/stories/${id}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  getPhotos: (id) => api.get(`/stories/${id}/photos`),
  updatePhoto: (storyId, photoId, data) =>
    api.put(`/stories/${storyId}/photos/${photoId}`, data),
  deletePhoto: (storyId, photoId) =>
    api.delete(`/stories/${storyId}/photos/${photoId}`),
  // Attachments (documents/files)
  uploadAttachments: (id, formData) =>
    api.post(`/stories/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  getAttachments: (id) => api.get(`/stories/${id}/attachments`),
  deleteAttachment: (storyId, attachmentId) =>
    api.delete(`/stories/${storyId}/attachments/${attachmentId}`),
  exportPDF: (id, options = {}) => {
    const params = new URLSearchParams(options).toString();
    return `${API_URL}/stories/${id}/export/pdf?${params}`;
  },
  exportMemoryBook: (familyCircleId, options = {}) => {
    const params = new URLSearchParams(options).toString();
    return `${API_URL}/stories/family/${familyCircleId}/export/pdf?${params}`;
  },
  setTimeCapsule: (id, data) => api.put(`/stories/${id}/time-capsule`, data),
  // Comments
  getComments: (storyId) => api.get(`/stories/${storyId}/comments`),
  addComment: (storyId, data) => api.post(`/stories/${storyId}/comments`, data),
  editComment: (storyId, commentId, data) => api.put(`/stories/${storyId}/comments/${commentId}`, data),
  deleteComment: (storyId, commentId) => api.delete(`/stories/${storyId}/comments/${commentId}`),
  reactToComment: (storyId, commentId, emoji) => api.post(`/stories/${storyId}/comments/${commentId}/react`, { emoji }),

  // Story Linking
getLinkedStories: (storyId) => api.get(`/stories/${storyId}/linked`),
linkStory: (storyId, data) => api.post(`/stories/${storyId}/link`, data),
unlinkStory: (storyId, targetStoryId) => api.delete(`/stories/${storyId}/link/${targetStoryId}`),

updateCategory: (id, category) => api.put(`/stories/${id}/category`, { category }),
updateTags: (id, tags) => api.put(`/stories/${id}/tags`, { tags }),
reassign: (id, newStorytellerId, storytellerType = 'user') =>
  api.put(`/stories/${id}/reassign`, { newStorytellerId, storytellerType }),
getTreeOnlyMembers: (familyId) => api.get(`/stories/family/${familyId}/tree-only-members`),

};

// Voice
export const voiceAPI = {
  getProfiles: (familyCircleId) => api.get(`/voice/profiles/${familyCircleId}`),
  createClone: (formData) => 
    api.post('/voice/clone', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  synthesize: (storyId, voiceProfileId, version = 'literary') =>
    api.post(`/voice/synthesize/${storyId}`, { voiceProfileId, version }),
  getSynthesizedUrl: (storyId, version) =>
    api.get(`/voice/synthesized/${storyId}/${version}`),
  getSubscription: () => api.get('/voice/subscription'),
  cloneFromStory: (storyId, data) => api.post(`/voice/clone-from-story/${storyId}`, data),
};

// Invites
export const inviteAPI = {
  send: (data) => api.post('/invites', data),
  getByFamily: (familyCircleId) => api.get(`/invites/family/${familyCircleId}`),
  getByToken: (token) => api.get(`/invites/${token}`),
  accept: (token) => api.post(`/invites/${token}/accept`),
  cancel: (id) => api.delete(`/invites/${id}`),
};

// Prompts
export const promptsAPI = {
  getAll: (params) => api.get('/prompts', { params }),
  getCategories: (lang) => api.get('/prompts/categories', { params: { lang } }),
  getRandom: (count, category, lang) => api.get('/prompts/random', { params: { count, category, lang } }),
  create: (data) => api.post('/prompts', data),
  trackUsage: (id) => api.post(`/prompts/${id}/use`),
  delete: (id) => api.delete(`/prompts/${id}`),
};

// Comments
export const commentsAPI = {
  getComments: (storyId) => api.get(`/stories/${storyId}/comments`),
  addComment: (storyId, data) => api.post(`/stories/${storyId}/comments`, data),
  editComment: (storyId, commentId, data) => api.put(`/stories/${storyId}/comments/${commentId}`, data),
  deleteComment: (storyId, commentId) => api.delete(`/stories/${storyId}/comments/${commentId}`),
  reactToComment: (storyId, commentId, emoji) => api.post(`/stories/${storyId}/comments/${commentId}/react`, { emoji }),
};

// Notifications
export const notificationsAPI = {
  getSettings: () => api.get('/notifications/settings'),
  updateSettings: (data) => api.put('/notifications/settings', data),
};

// Billing
export const billingAPI = {
  getSubscription: () => api.get('/billing/subscription'),
  createCheckout: (tier, interval) => api.post('/billing/create-checkout', { tier, interval }),
  createPortal: () => api.post('/billing/create-portal'),
  verifyMobile: (data) => api.post('/billing/verify-mobile', data),
  restoreMobile: (data) => api.post('/billing/restore-mobile', data),
};

export default api;